import Image from "next/image"
import { Anchor } from "../../app/components/anchor"
import Avatar from "../../assets/icons/avatar.svg"
import { getCityUrl } from "../../util/getCityUrl"
import { getBreedUrl } from "../../util/getBreedUrl"
import DistanceIcon from "../../assets/images/pet-shipping/distanceIcon.webp"
import {
  getNumericValue,
  getNumericValueFromDistance,
  getPriceValue,
  localizeNumber,
  roundRating,
} from "../../util/formatNumber"

interface IPetCard {
  image: any
  tags: string[]
  price: string
  customer: {
    image: string
    name: string
    rating: string
  }
  animalName: string
  breed: string
  pickupCity: string
  pickupStateCode: string
  pickupZipCode?: string
  deliverCity: string
  deliverStateCode: string
  deliverZipCode?: string
  type: "export" | "import"
  subType: string
  photo?: string
  currentLocation?: string // Add this to pass current page's city and state
}

export default function PetCard({
  image,
  tags,
  price,
  customer,
  animalName,
  breed,
  pickupCity,
  pickupStateCode,
  pickupZipCode,
  deliverCity,
  deliverStateCode,
  deliverZipCode,
  type,
  subType,
  photo,
  currentLocation,
}: IPetCard) {
  const formattedPickupLocation = `${pickupCity}, ${pickupStateCode}`
  const formattedDeliveryLocation = `${deliverCity}, ${deliverStateCode}`

  return (
    <section>
      <div className="border border-1 border-gray200 rounded-xl md:rounded-md md:flex px-4 mb-8 items-center">
        <div className="md:border-r border-1 border-gray200 py-4 px-1 md:w-[420px] md:h-44">
          <p className="mb-3">
            {getBreedUrl(breed) ? (
              <Anchor href={getBreedUrl(breed)} prefetch={false}>
                {breed}
              </Anchor>
            ) : (
              breed
            )}{" "}
            <b>from </b>{" "}
            {currentLocation !== formattedPickupLocation ? (
              <a href={getCityUrl(formattedPickupLocation)}>
                {formattedPickupLocation}
              </a>
            ) : (
              formattedPickupLocation
            )}{" "}
            {type === "export" ? pickupZipCode : null} to{" "}
            {currentLocation !== formattedDeliveryLocation ? (
              <a href={getCityUrl(formattedDeliveryLocation)}>
                {formattedDeliveryLocation}
              </a>
            ) : (
              formattedDeliveryLocation
            )}{" "}
            {type === "import" ? deliverZipCode : null}
          </p>
          <div className="flex gap-1">
            {subType && (
              <p className="px-2 mb-2 md:px-4 py-1 text-sm flex items-center justify-center bg-gray-100 rounded-full">
                {subType}
              </p>
            )}
            {tags[0] && (
              <p className="px-2 mb-2 md:px-4 py-1 text-sm flex items-center justify-center bg-gray-100 rounded-full">
                {tags[0]}
              </p>
            )}
            <p className="px-2 mb-2 md:px-4 py-1 text-sm flex items-center justify-center bg-gray-100 rounded-full">
              <Image className="mr-2" src={DistanceIcon} alt="ship boats" />
              {tags[1]?.toLocaleString()} mi
            </p>
          </div>
          <p className="md:hidden font-montserrat font-medium text-lg">
            {price && localizeNumber(getNumericValue(price) as number)}
          </p>
          <div className="flex gap-1 mt-1 pt-0.5">
            <Image
              className="h-11 w-11 rounded-full object-cover overflow-hidden border border-1 border-gray-100"
              src={photo || Avatar.src}
              alt={customer.name}
              height={44}
              width={44}
              loading="lazy"
            />

            <div className="ml-2">
              <h6 className="flex">{customer.name}</h6>
              <p className="text-xs text-ellipsis">
                {roundRating(Number(customer.rating))} of 5 stars
              </p>
            </div>
          </div>
        </div>
        <div className="py-4 px-1 m-auto items-center hidden md:flex md:flex-col">
          <p className="mb-0 font-medium text-lg lg:text-2xl">
            {price && localizeNumber(getPriceValue(price) as number)}
          </p>
          <small className="mt-1">
            $
            {(
              Number(getPriceValue(price)) /
              Number(getNumericValueFromDistance(tags[1]))
            ).toFixed(3)}
            /mile
          </small>
        </div>
      </div>
    </section>
  )
}
